import moment from 'moment';
import EventBus from '../event-bus';

/**
 * Fechas de inicio con las que se busca imágenes GIS para un rango: el pedido
 * (por defecto, el último mes) y, si no hay ninguna imagen, 3 meses y 1 año
 * antes del fin. Tope en 1 año para no generar gráficos de varios años en
 * parcelas sin actividad reciente. Lo comparten el mapa (GISWMSTimeDimensionMap)
 * y la línea temporal (Gis/fetchTimelineIndexes) para que amplíen igual.
 * @param {String|moment.Moment} requestedStartDate
 * @param {String|moment.Moment} endDate
 * @return {Array<moment.Moment>}
 */
export function getFallbackStartDates(requestedStartDate, endDate) {
  return [
    moment(requestedStartDate),
    moment(endDate).subtract(3, 'months'),
    moment(endDate).subtract(1, 'year'),
  ];
}

/**
 * Igual que getFallbackStartDates pero descartando las ampliaciones que no
 * empiezan antes que el rango pedido: serían un subconjunto de un rango ya
 * vacío y solo añadirían llamadas a GIS.
 * @param {String} requestedStartDate 'YYYY-MM-DD'
 * @param {String} endDate 'YYYY-MM-DD'
 * @return {Array<String>} Fechas de inicio 'YYYY-MM-DD'
 */
export function getWideningStartDates(requestedStartDate, endDate) {
  const [requested, ...fallbacks] = getFallbackStartDates(requestedStartDate, endDate);
  return [
    requested,
    ...fallbacks.filter((startDate) => startDate.isBefore(requested, 'day')),
  ].map((startDate) => startDate.format('YYYY-MM-DD'));
}

/**
 * Evento con el que GISLayerOptions refleja en su selector un rango ampliado. Se usa
 * un evento y no un watcher porque las claves de currentInitIntervalDates se añaden
 * al store sin Vue.set y no son reactivas.
 * @param {String} keyId
 * @return {String}
 */
export function timelineRangeWidenedEvent(keyId) {
  return `timelineRangeWidened${keyId}`;
}

/**
 * Cuando la línea temporal se ha cargado con un rango más amplio que el seleccionado
 * (porque este no tenía imágenes), aplica ese rango a toda la pestaña: lo guarda en el
 * store, lo refleja en el selector de fechas, recarga el mapa, las tareas y las
 * observaciones con él, y avisa al usuario (EDSER-2138).
 * @param {Vue} vm Componente desde el que se aplica
 * @param {String} keyId Clave del mapa en el store (p. ej. 'GisTimeDimension')
 * @param {{initDate: String, endDate: String, widened: Boolean}} result Lo que devuelve Gis/fetchTimelineIndexes
 * @return {Boolean} Si se ha ampliado el rango
 */
export function applyWidenedTimelineRange(vm, keyId, result) {
  if (!result?.widened) return false;

  vm.$store.dispatch('Gis/setCurrentInitIntervalDates', {
    key: keyId,
    newCurrentInitIntervalDate: `${result.initDate}T00:00:00`,
  });
  EventBus.$emit(timelineRangeWidenedEvent(keyId), result);
  EventBus.$emit(`updateTimeDimension${keyId}`);
  EventBus.$emit('filteringTasks');

  vm.$notifyDX(
    {
      // i18next escapa los valores como HTML ("/" -> "&#x2F;") y el aviso muestra texto
      // plano; la fecha la formateamos nosotros, así que no hace falta escaparla
      message: vm.$t('Gis.TimelineRangeWidened', {
        date: moment(result.initDate).format('DD/MM/YYYY'),
        interpolation: { escapeValue: false },
      }),
      width: 550,
    },
    'info',
    6000,
  );
  return true;
}
